import { Component, OnDestroy, OnInit, input, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { ApContentService, ApContentPipe, ApKeyOverridePipe } from '@ap/content';
import { ErrorMessages } from '@ap/form/errors';
import { ApRadioGroupComponent, ApRadioButtonComponent } from '@ap/radio';
import { ApFormControlPipe } from '@ap/cdk';
import { ApAmountInputComponent } from '@ap/input/amount';

import content from '../income.content.json';
import { EntrepreneurIncomeForm } from '../income.model';

@Component({
    selector: 'ap-entrepreneur-income',
    templateUrl: './entrepreneur-income.component.html',
    imports: [
        ApRadioGroupComponent,
        ApRadioButtonComponent,
        FormsModule,
        ReactiveFormsModule,
        ApAmountInputComponent,
        ApFormControlPipe,
        ApContentPipe,
        ApKeyOverridePipe
    ]
})
export class ApEntrepreneurIncomeComponent implements OnInit, OnDestroy {
    contentService = inject(ApContentService);

    public readonly numberOfDecimals = input(2);
    public readonly contentKeyMap = input({});
    public readonly form = input.required<FormGroup<EntrepreneurIncomeForm>>();

    public taxableProfitErrorMessages: ErrorMessages;
    public currentYear = new Date().getFullYear();
    private nrOfFinancialYearsSubscription: Subscription;

    public ngOnInit(): void {
        this.contentService.addContentItems(content);
        this.nrOfFinancialYearsSubscription = this.form().controls.nrOfFinancialYears.valueChanges.subscribe((nrOfFinancialYear) => {
            this.updateTaxableProfitControls(nrOfFinancialYear);
        });
        this.setErrorMessages();
    }

    public ngOnDestroy(): void {
        this.nrOfFinancialYearsSubscription.unsubscribe();
    }

    private setErrorMessages(): void {
        this.taxableProfitErrorMessages = {
            required: 'income.input.taxableProfit.error.required',
            min: 'income.input.taxableProfit.error.min',
            max: 'income.input.taxableProfit.error.max'
        };
    }

    private updateTaxableProfitControls(nrOfFinancialYears: number): void {
        this.updateTaxableProfitLastYearControl(nrOfFinancialYears);
        this.updateTaxableProfitLastYearMinusOneControl(nrOfFinancialYears);
        this.updateTaxableProfitLastYearMinusTwoControl(nrOfFinancialYears);
    }

    private updateTaxableProfitLastYearMinusTwoControl(nrOfFinancialYears: number) {
        if (nrOfFinancialYears >= 3) {
            this.form().controls.taxableProfitLastYearMinusTwo.addValidators(Validators.required);
        } else {
            this.form().controls.taxableProfitLastYearMinusTwo.clearValidators();
        }
        this.form().controls.taxableProfitLastYearMinusTwo.markAsPristine();
        this.form().controls.taxableProfitLastYearMinusTwo.markAsUntouched();
        this.form().controls.taxableProfitLastYearMinusTwo.updateValueAndValidity();
    }

    private updateTaxableProfitLastYearMinusOneControl(nrOfFinancialYears: number) {
        if (nrOfFinancialYears >= 2) {
            this.form().controls.taxableProfitLastYearMinusOne.addValidators(Validators.required);
        } else {
            this.form().controls.taxableProfitLastYearMinusOne.clearValidators();
        }
        this.form().controls.taxableProfitLastYearMinusOne.markAsPristine();
        this.form().controls.taxableProfitLastYearMinusOne.markAsUntouched();
        this.form().controls.taxableProfitLastYearMinusOne.updateValueAndValidity();
    }

    private updateTaxableProfitLastYearControl(nrOfFinancialYears: number) {
        if (nrOfFinancialYears >= 1) {
            this.form().controls.taxableProfitLastYear.addValidators(Validators.required);
            this.form().controls.taxableProfitLastYear.markAsPristine();
            this.form().controls.taxableProfitLastYear.markAsUntouched();
            this.form().controls.taxableProfitLastYear.updateValueAndValidity();
        }
    }
}
